import { Check, CheckCircle } from '@phosphor-icons/react'
import {
  Children,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  useCallback,
} from 'react'
import { type BaseBoxProps, Box } from '../../Box'
import { Icon } from '../../Icon'
import { Row } from '../../Row'
import { TextWithCapsize as Text, type TextWithCapsizeProps } from '../../Text'
import { useSelectContext } from './hooks/useSelectContext'
import {
  iconCss,
  listItemCss,
  listItemV4Css,
  listItemHeaderCss,
} from './Option.css'

export type OptionProps<T> = {
  value: T
  children: ReactNode
  disabled?: boolean
  version?: '3' | '4'
} & Omit<HTMLAttributes<HTMLLIElement>, keyof BaseBoxProps>

function wrapChildrenWithText(
  children: ReactNode,
  textProps: Partial<TextWithCapsizeProps>,
) {
  return Children.map(children, (child, index) => {
    if (typeof child === 'string') {
      return (
        <Text variant={null} key={index} {...textProps}>
          {child}
        </Text>
      )
    }

    return child
  })
}

export type OptionGroupProps = {
  children: Array<ReactElement<OptionProps<any>, typeof Option<any>>>
  group: string
}
function OptionGroup({ children, group }: OptionGroupProps) {
  return (
    <Box role="optgroup" aria-labelledby={group}>
      <Text size="12" variant="secondary" className={listItemHeaderCss}>
        {group}
      </Text>
      {children}
    </Box>
  )
}

OptionGroup.displayName = 'OptionGroup'

function Option<T>({
  children,
  value,
  disabled,
  version = '3',
  ...props
}: OptionProps<T>) {
  const { selected, setSelected, focused } = useSelectContext()
  const onClickHandler = useCallback(() => {
    if (disabled) {
      return
    }

    setSelected(value)
  }, [disabled, setSelected, value])

  const isSelected = selected.has(value)
  const isFocused = focused === value

  return (
    <Row
      {...props}
      as="li"
      className={{
        [listItemCss]: version === '3',
        [listItemV4Css]: version === '4',
      }}
      tabIndex={-1}
      onClick={onClickHandler}
      role="option"
      data-selected={isSelected}
      data-focused={isFocused}
      data-disabled={disabled}
    >
      <Row start>
        {wrapChildrenWithText(children, {
          size: version === '3' ? '12' : '14',
          style: {
            fontWeight: 400,
          },
        })}
      </Row>
      <Box
        as="span"
        className={iconCss}
        opacity={isSelected ? 1 : 0}
        fixWhiteSpace
      >
        <Icon
          width="16"
          height="16"
          as={version === '4' ? Check : CheckCircle}
          opacity={1}
        />
      </Box>
    </Row>
  )
}

Option.displayName = 'Option'

export { OptionGroup, Option }
